import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Play, 
  Clock, 
  Target, 
  CheckCircle, 
  Circle, 
  Square,
  Type,
  ArrowUpDown,
  Eye,
  Award
} from 'lucide-react';
import { Button } from '../../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Badge } from '../../ui/badge';
import { Progress } from '../../ui/progress';
import { ScrollArea } from '../../ui/scroll-area';
import { type Quiz } from '../../../services/elearningQuizService';

interface QuizPreviewProps {
  quiz: Quiz;
}

const QuizPreview: React.FC<QuizPreviewProps> = ({ quiz }) => {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState<Record<string, string[]>>({});
  const [showResults, setShowResults] = useState(false);

  const questions = quiz.questions || [];
  const currentQuestion = questions[currentQuestionIndex];
  const totalQuestions = questions.length;
  const progress = totalQuestions > 0 ? ((currentQuestionIndex + 1) / totalQuestions) * 100 : 0;

  const getQuestionIcon = (type: string) => {
    switch (type) {
      case 'single_choice': return Circle;
      case 'multiple_choice': return CheckCircle;
      case 'true_false': return Square;
      case 'text_input': return Type;
      case 'ordering': return ArrowUpDown;
      default: return Circle;
    }
  };

  const handleAnswerSelect = (questionId: string, answerId: string, isMultiple: boolean) => {
    setSelectedAnswers(prev => {
      if (isMultiple) {
        const current = prev[questionId] || [];
        const newAnswers = current.includes(answerId)
          ? current.filter(id => id !== answerId)
          : [...current, answerId];
        return { ...prev, [questionId]: newAnswers };
      } else {
        return { ...prev, [questionId]: [answerId] };
      }
    });
  };

  const nextQuestion = () => {
    if (currentQuestionIndex < totalQuestions - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
    } else {
      setShowResults(true);
    }
  };

  const prevQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
    }
  };

  const resetPreview = () => {
    setCurrentQuestionIndex(0);
    setSelectedAnswers({});
    setShowResults(false);
  };

  const formatTime = (seconds: number | null) => {
    if (!seconds) return 'Aucune limite';
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return minutes > 0 ? `${minutes}m ${remainingSeconds}s` : `${remainingSeconds}s`;
  };

  if (totalQuestions === 0) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
            <Eye className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-medium text-white mb-2">Aucune question à prévisualiser</h3>
          <p className="text-gray-400">Ajoutez des questions pour voir l'aperçu du quiz</p>
        </div>
      </div>
    );
  }

  if (showResults) {
    const totalPoints = questions.reduce((sum, q) => sum + q.points, 0);
    const earnedPoints = questions.reduce((sum, q) => {
      const userAnswers = selectedAnswers[q.id] || [];
      const correctAnswers = q.answers?.filter(a => a.is_correct).map(a => a.id) || [];
      
      if (q.question_type === 'multiple_choice') {
        const isCorrect = correctAnswers.length === userAnswers.length &&
                          correctAnswers.every(id => userAnswers.includes(id));
        return sum + (isCorrect ? q.points : 0);
      } else {
        const isCorrect = userAnswers.length > 0 && correctAnswers.includes(userAnswers[0]);
        return sum + (isCorrect ? q.points : 0);
      }
    }, 0);

    const percentage = totalPoints > 0 ? (earnedPoints / totalPoints) * 100 : 0;
    const passed = percentage >= quiz.passing_score;

    return (
      <div className="h-full flex items-center justify-center p-6">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-md w-full"
        >
          <Card className="bg-gradient-to-br from-gray-800 to-gray-900 border-gray-700 text-center">
            <CardHeader>
              <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${
                passed ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
              }`}>
                <Award className="w-8 h-8" />
              </div>
              <CardTitle className="text-2xl text-white">
                {passed ? 'Quiz Réussi !' : 'Quiz Échoué'}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">Score obtenu</span>
                  <span className="text-white font-medium">{Math.round(percentage)}%</span>
                </div>
                <Progress 
                  value={percentage} 
                  className={`h-3 ${passed ? 'bg-green-500/20' : 'bg-red-500/20'}`}
                />
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">Points</span>
                  <span className="text-white">{earnedPoints} / {totalPoints}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">Score requis</span>
                  <span className="text-white">{quiz.passing_score}%</span>
                </div>
              </div>

              <Button
                onClick={resetPreview}
                className="w-full bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700"
              >
                Recommencer l'aperçu
              </Button>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    );
  }

  const IconComponent = getQuestionIcon(currentQuestion.question_type);

  return (
    <div className="h-full flex flex-col">
      {/* Header du quiz */}
      <div className="p-6 border-b border-gray-700">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-xl font-bold text-white">{quiz.title}</h3>
            <p className="text-gray-400">{quiz.description}</p>
          </div>
          <div className="flex items-center space-x-4">
            {quiz.time_limit && (
              <Badge variant="outline" className="border-orange-500 text-orange-400">
                <Clock className="w-3 h-3 mr-1" />
                {formatTime(quiz.time_limit)}
              </Badge>
            )}
            <Badge variant="outline" className="border-green-500 text-green-400">
              <Target className="w-3 h-3 mr-1" />
              {quiz.passing_score}% requis
            </Badge>
          </div>
        </div>

        {/* Barre de progression */}
        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-gray-400">Question {currentQuestionIndex + 1} sur {totalQuestions}</span>
            <span className="text-white">{Math.round(progress)}%</span>
          </div>
          <Progress value={progress} className="h-2" />
        </div>
      </div>

      {/* Question actuelle */}
      <ScrollArea className="flex-1 p-6">
        <motion.div
          key={currentQuestionIndex}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.3 }}
        >
          <Card className="bg-gray-800/50 border-gray-700">
            <CardHeader>
              <div className="flex items-start space-x-3">
                <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
                  <IconComponent className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-2">
                    <Badge variant="outline" className="border-blue-500 text-blue-400">
                      {currentQuestion.question_type.replace('_', ' ')}
                    </Badge>
                    <Badge variant="outline" className="border-green-500 text-green-400">
                      {currentQuestion.points} point{currentQuestion.points !== 1 ? 's' : ''}
                    </Badge>
                  </div>
                  <h4 className="text-lg font-medium text-white leading-relaxed">
                    {currentQuestion.question_text}
                  </h4>
                  {currentQuestion.explanation && (
                    <p className="text-gray-400 text-sm mt-2">
                      💡 {currentQuestion.explanation}
                    </p>
                  )}
                </div>
              </div>
            </CardHeader>

            <CardContent>
              <div className="space-y-3">
                {currentQuestion.answers?.map((answer, index) => {
                  const isSelected = selectedAnswers[currentQuestion.id]?.includes(answer.id) || false;
                  const isMultiple = currentQuestion.question_type === 'multiple_choice';
                  
                  return (
                    <motion.div
                      key={answer.id}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <button
                        onClick={() => handleAnswerSelect(currentQuestion.id, answer.id, isMultiple)}
                        className={`w-full text-left p-4 rounded-lg border-2 transition-all duration-200 ${
                          isSelected
                            ? 'border-blue-500 bg-blue-500/10 text-blue-300'
                            : 'border-gray-600 bg-gray-700/30 text-gray-300 hover:border-gray-500'
                        }`}
                      >
                        <div className="flex items-center space-x-3">
                          <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                            isSelected ? 'border-blue-500 bg-blue-500' : 'border-gray-500'
                          }`}>
                            {isSelected && (
                              <div className="w-2 h-2 bg-white rounded-full" />
                            )}
                          </div>
                          <span className="font-medium">{answer.answer_text}</span>
                        </div>
                      </button>
                    </motion.div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </ScrollArea>

      {/* Navigation */}
      <div className="p-6 border-t border-gray-700">
        <div className="flex items-center justify-between">
          <Button
            variant="outline"
            onClick={prevQuestion}
            disabled={currentQuestionIndex === 0}
            className="border-gray-600 text-gray-400 hover:bg-gray-700 disabled:opacity-50"
          >
            Précédent
          </Button>

          <div className="flex items-center space-x-2">
            <Button
              onClick={resetPreview}
              variant="ghost"
              className="text-gray-400 hover:text-white"
            >
              Recommencer
            </Button>
            <Button
              onClick={nextQuestion}
              className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white/70 hover:text-white"
            >
              {currentQuestionIndex === totalQuestions - 1 ? 'Terminer' : 'Suivant'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export { QuizPreview };
